import React, { useState, useRef, useEffect } from 'react';
import VariablePicker from '../components/variable_Picker';
import { useVariables } from '../contexts/VariableContext';
import { resolveVariables } from '../utils/variableResolver';

// Calculator Field Component
const CalculatorField = ({ 
  generatedKey,
  placeholder = "Enter formula...", 
  className = "" 
}) => {
  const { variableDict, updateVariable, updateTrigger } = useVariables();
  const [showPicker, setShowPicker] = useState(false);
  const [pickerPosition, setPickerPosition] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [pickerType, setPickerType] = useState('variables'); // 'variables' or 'functions'
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);
  const editorRef = useRef(null);
  const lastTriggerRef = useRef(updateTrigger);

  // Math functions as virtual variables
  const mathFunctions = {
    sqrt: { name: 'sqrt', value: 'sqrt(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    pow: { name: 'pow', value: 'pow(x, y)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    abs: { name: 'abs', value: 'abs(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    ceil: { name: 'ceil', value: 'ceil(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    floor: { name: 'floor', value: 'floor(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    round: { name: 'round', value: 'round(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    min: { name: 'min', value: 'min(x, y)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    max: { name: 'max', value: 'max(x, y)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    sin: { name: 'sin', value: 'sin(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    cos: { name: 'cos', value: 'cos(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
    tan: { name: 'tan', value: 'tan(x)', color: 'bg-pink-100 text-pink-800 border-pink-300' },
  };

  // Listen for variable updates and refresh badges
  useEffect(() => {
    if (updateTrigger !== lastTriggerRef.current && editorRef.current) {
      lastTriggerRef.current = updateTrigger;
      
      // Update all variable badges to show current values
      const badges = editorRef.current.querySelectorAll('[data-variable]');
      badges.forEach(badge => {
        const varKey = badge.dataset.variable;
        if (variableDict[varKey]) {
          badge.textContent = String(variableDict[varKey].value || varKey);
        }
      });
      
      // Re-evaluate with updated values
      evaluateFormula();
      
      // Update stored value
      if (generatedKey && variableDict[generatedKey]) {
        const rawValue = getValue();
        if (variableDict[generatedKey].value !== rawValue) {
          updateVariable(generatedKey, rawValue);
        }
      }
    }
  }, [updateTrigger, variableDict]);

  const evaluateFormula = () => {
    if (!editorRef.current) return;
    
    try {
      const rawFormula = getValue(); // Get formula with @variable references
      const resolvedFormula = resolveVariables(rawFormula, variableDict); // Resolve to actual values
      
      if (resolvedFormula.trim()) {
        // Replace simple function names with Math equivalents
        const mathFunctionNames = ['sqrt', 'pow', 'abs', 'ceil', 'floor', 'round', 'min', 'max', 'sin', 'cos', 'tan', 'log', 'exp'];
        let processedFormula = resolvedFormula;
        
        mathFunctionNames.forEach(func => {
          const regex = new RegExp(`\\b${func}\\(`, 'g');
          processedFormula = processedFormula.replace(regex, `Math.${func}(`);
        });
        
        // Evaluate using Function constructor
        const calculatedResult = new Function('return ' + processedFormula)();
        setResult(calculatedResult);
        setError(null);
      } else {
        setResult(null);
        setError(null);
      }
    } catch (e) {
      setError('Invalid formula');
      setResult(null);
    }
  };

  const handleInput = (e) => {
    const text = e.target.textContent;
    
    // Store raw value with @variable references
    const rawValue = getValue();
    if (generatedKey && variableDict[generatedKey]) {
      updateVariable(generatedKey, rawValue);
    }
    
    // Auto-evaluate on input change
    evaluateFormula();
    
    // Check if @ is typed (for variables)
    if (text.endsWith('@')) {
      setShowPicker(true);
      setSearchTerm('');
      setPickerType('variables');
      
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      const rect = range.getBoundingClientRect();
      
      setPickerPosition({
        top: rect.bottom + window.scrollY + 5,
        left: rect.left + window.scrollX
      });
    } else if (text.match(/[a-zA-Z]$/)) {
      // Check if a letter is typed (for math functions)
      const words = text.match(/[a-zA-Z]+$/);
      if (words && words[0]) {
        const currentWord = words[0];
        
        // Only show picker if word matches potential function names
        const potentialMatches = Object.keys(mathFunctions).filter(func => 
          func.startsWith(currentWord.toLowerCase())
        );
        
        if (potentialMatches.length > 0) {
          setShowPicker(true);
          setSearchTerm(currentWord);
          setPickerType('functions');
          
          const selection = window.getSelection();
          const range = selection.getRangeAt(0);
          const rect = range.getBoundingClientRect();
          
          setPickerPosition({
            top: rect.bottom + window.scrollY + 5,
            left: rect.left + window.scrollX
          });
        } else {
          setShowPicker(false);
        }
      }
    } else if (showPicker && pickerType === 'variables') {
      const atIndex = text.lastIndexOf('@');
      if (atIndex !== -1) {
        const term = text.substring(atIndex + 1);
        setSearchTerm(term);
      } else {
        setShowPicker(false);
      }
    } else if (!text.match(/[a-zA-Z@]$/)) {
      setShowPicker(false);
    }
  };

  const insertVariable = (varKey) => {
    const editor = editorRef.current;
    const selection = window.getSelection();
    const text = editor.textContent;
    
    if (pickerType === 'functions') {
      // For functions, replace the partial word
      const words = text.match(/[a-zA-Z]+$/);
      if (words && words[0]) {
        const wordStart = text.lastIndexOf(words[0]);
        
        editor.innerHTML = '';
        const beforeWord = text.substring(0, wordStart);
        
        if (beforeWord) {
          const textNode = document.createTextNode(beforeWord);
          editor.appendChild(textNode);
        }
        
        const funcText = document.createTextNode(varKey + '(');
        editor.appendChild(funcText);
        
        const range = document.createRange();
        range.setStartAfter(funcText);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    } else {
      // For variables
      const atIndex = text.lastIndexOf('@');
      
      editor.innerHTML = '';
      
      const beforeAt = text.substring(0, atIndex);
      
      if (beforeAt) {
        const textNode = document.createTextNode(beforeAt);
        editor.appendChild(textNode);
      }
      
      // Create variable badge
      const badge = document.createElement('span');
      badge.contentEditable = 'false';
      badge.className = `inline-flex items-center px-2 py-0.5 mx-0.5 rounded border ${variableDict[varKey]?.color || 'bg-gray-200'} text-sm font-medium cursor-default`;
      badge.textContent = String(variableDict[varKey]?.value || varKey);
      badge.dataset.variable = varKey;
      badge.title = `@${varKey}`;
      editor.appendChild(badge);
      
      const spaceNode = document.createTextNode('');
      editor.appendChild(spaceNode);
      
      const range = document.createRange();
      range.setStartAfter(spaceNode);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
    }
    
    setShowPicker(false);
    setSearchTerm('');
    editor.focus();
    
    // Update stored value and evaluate
    const newRawValue = getValue();
    if (generatedKey) {
      updateVariable(generatedKey, newRawValue);
    }
    setTimeout(evaluateFormula, 0);
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Escape') {
      setShowPicker(false);
    }
    
    if (e.key === 'Enter') {
      e.preventDefault();
      evaluateFormula();
    }
    
    if (showPicker && ['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) {
      return;
    }
    
    if (e.key === 'Backspace') {
      const selection = window.getSelection();
      const range = selection.getRangeAt(0);
      
      if (range.collapsed) {
        const node = range.startContainer;
        const offset = range.startOffset;
        
        if (node.previousSibling && node.previousSibling.dataset && node.previousSibling.dataset.variable && offset === 0) {
          e.preventDefault();
          node.previousSibling.remove();
          
          const newRawValue = getValue();
          if (generatedKey) {
            updateVariable(generatedKey, newRawValue);
          }
          setTimeout(evaluateFormula, 0);
        }
      }
    }
  };

  const clear = () => {
    if (editorRef.current) {
      editorRef.current.innerHTML = '';
      setResult(null);
      setError(null);
      editorRef.current.focus();
      
      if (generatedKey) {
        updateVariable(generatedKey, '');
      }
    }
  };

  // Get value WITH variable references (@variable syntax)
  const getValue = () => {
    if (!editorRef.current) return '';
    
    let formula = '';
    const children = editorRef.current.childNodes;
    
    children.forEach(node => {
      if (node.nodeType === Node.TEXT_NODE) {
        formula += node.textContent;
      } else if (node.dataset && node.dataset.variable) {
        formula += `@${node.dataset.variable}`;
      }
    });
    
    return formula.trim();
  };

  // Get the RESOLVED value (with actual variable values substituted)
  const getActualValue = () => {
    const rawValue = getValue();
    return resolveVariables(rawValue, variableDict);
  };

  // Filter out current field's key from variables
  const getFilteredVariables = () => {
    const { [generatedKey]: _, ...filteredDict } = variableDict;
    return filteredDict;
  };

  // Export result as a getter
  const getResult = () => result;

  return (
    <div className="space-y-3">
      <div className="relative">
        <div
          ref={editorRef}
          contentEditable
          onInput={handleInput}
          onKeyDown={handleKeyDown}
          className={`min-h-[60px] px-4 py-3 border-2 border-slate-300 rounded-lg focus:outline-none focus:border-blue-500 transition-colors bg-white font-mono text-lg ${className}`}
          style={{ 
            whiteSpace: 'pre-wrap',
            wordBreak: 'break-word'
          }}
          suppressContentEditableWarning
        />
        
        {/* Placeholder */}
        {editorRef.current && editorRef.current.textContent === '' && (
          <div className="absolute top-3 left-4 text-slate-400 pointer-events-none font-mono">
            {placeholder}
          </div>
        )}

        {/* Variable Picker */}
        {showPicker && (
          <VariablePicker
            variables={pickerType === 'variables' ? getFilteredVariables() : mathFunctions}
            position={pickerPosition}
            searchTerm={searchTerm}
            onSelect={insertVariable}
            onClose={() => setShowPicker(false)}
          />
        )}
      </div>

    </div>
  );
};

export default CalculatorField;